import * as React from 'react'
import clx from 'classnames'
import { useLocation } from 'react-router-dom'
import { FaIcon, faClose, faSquareArrowLeftSolid } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useMediaQuery } from '../hooks/useMediaQuery'

type Props = {
  children: React.ReactNode
  className?: string
  drawerClassName?: string
  
  breakpoint?: number
  closeOnNavigate?: boolean
}


export const CollapsibleSidePanel = ({
  children,
  className,
  drawerClassName,
  breakpoint = 1024,
  closeOnNavigate = true,
}: Props) => {
  const { tSafe } = useLocalize()
  const isSmallScreen = useMediaQuery(`(max-width: ${breakpoint - 1}px)`)
  const [open, setOpen] = React.useState(false)
  const location = useLocation()

  const showLabel = tSafe('shared.side-panel.show', 'Pokaż listę')
  const hideLabel = tSafe('shared.side-panel.hide', 'Ukryj listę')

  React.useEffect(() => {
    if (!isSmallScreen) setOpen(false)
  }, [isSmallScreen])

  React.useEffect(() => {
    if (closeOnNavigate) setOpen(false)
  }, [location.pathname, closeOnNavigate])

  React.useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  if (!isSmallScreen) {
    return <div className={className}>{children}</div>
  }

  return (
 
    <div className="tw-relative tw-flex tw-w-10 tw-flex-shrink-0 tw-flex-col tw-items-center tw-border-r tw-border-base-300 tw-pt-3">
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={showLabel}
        aria-label={showLabel}
        aria-expanded={open}
        className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-base-100 tw-shadow tw-transition hover:tw-bg-secondary"
      >
        <FaIcon
          icon={faSquareArrowLeftSolid}
          className="tw-h-4 tw-w-4 tw-rotate-180 tw-text-content-base"
        />
      </button>

      <div
        aria-hidden
        onClick={() => setOpen(false)}
        className={clx(
          'layer-overlay tw-fixed tw-inset-0 tw-bg-black/40 tw-transition-opacity tw-duration-200',
          open ? 'tw-opacity-100' : 'tw-pointer-events-none tw-opacity-0',
        )}
      />

      <aside
        className={clx(
          'layer-drawer tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-flex-col tw-overflow-hidden tw-border-r tw-border-base-300 tw-bg-base-100 tw-shadow-xl',
     
          'tw-transition-all tw-duration-200',
          open
            ? 'tw-visible tw-translate-x-0'
            : 'tw-invisible -tw-translate-x-full',
          drawerClassName ?? 'tw-w-[320px] tw-max-w-[85vw]',
        )}
      >
        <div className="tw-flex tw-justify-end tw-px-1 tw-pt-1">
          <button
            type="button"
            onClick={() => setOpen(false)}
            title={hideLabel}
            aria-label={hideLabel}
            className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-text-content-base tw-transition hover:tw-bg-secondary"
          >
            <FaIcon icon={faClose} className="tw-h-4 tw-w-4" />
          </button>
        </div>
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
          {children}
        </div>
      </aside>
    </div>
  )
}
